import React, { useState, useCallback, useContext } from 'react';
import { Icon, Input, Popover, Tooltip } from 'antd';
import { withTranslation } from 'react-i18next';
import {
  Container,
  BarContainer,
  IconButton,
  BarPopupWrapper,
  AccessibilityContent,
  LinkInputRow,
  LinkSaveButton,
  LinkCancelButton,
  LinkEditButton,
  LinkRemoveButton,
  LinkSavedContainer,
  LinkDisplayCard,
  LinkDisplayText,
  LinkActionRow,
  LinkErrorMessage,
} from './style';
import { AccessibilitySvgIcon } from '../../images/svgIcons';
import { CONTEXT_KEYS, useArtboardContext } from '../../context/complexArtboardContext';
import { useDocumentContext } from '../../context/complexDocumentContext';
import { PageElementsContext } from '../../context/pageElementsContext';
import AccessibilitySummaryEditor from '../../containers/SidebarMenu/AccessibilitySummaryEditor';
import { ACCESSIBILITY_MODAL } from './translationValues';

const NAMESPACES = ['ACCESSIBILITY_MODAL'];

const AccessibilityIcon = ({
  t,
  accessibilityVisible,
  linkVisible,
  setAccessibilityPopupVisible,
  elementUUID,
  staticTableBlock = false,
  showAccessibility,
  showImageLink,
  setImageLinkPopupVisible,
  dataElementType,
}) => {
  const customCurrentBlockSelector = useCallback(store => store[CONTEXT_KEYS.CURRENT_BLOCK], []);
  const [currentEditingBlock] = useArtboardContext(customCurrentBlockSelector);

  const [, store] = useDocumentContext(() => null, `Accessibility`);

  const contextSelector = useCallback(store => store, []);
  const [documentContext] = useDocumentContext(contextSelector);

  const { updateComplexElementDetails } = useContext(PageElementsContext);

  const accessibilityInfo = staticTableBlock
    ? store.get()[elementUUID]?.style
    : store.get()[currentEditingBlock]?.contents.find(content => content.uuid === elementUUID)
        ?.style;

  const [inputValue, setInputValue] = useState(accessibilityInfo?.hyperlinkValue || '');
  const [isEditing, setIsEditing] = useState(false);
  const [urlError, setUrlError] = useState('');

  const isValidUrl = value => {
    try {
      const url = new URL(value);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const updateAccessibleSummary = summaryContent => {
    updateComplexElementDetails(elementUUID, { summary: summaryContent.contents }, 'style');
  };

  const updateImageRedirectUrl = url => {
    updateComplexElementDetails(elementUUID, { hyperlinkValue: url }, 'style');
  };

  const handleSave = () => {
    if (!inputValue.trim()) return;
    if (!isValidUrl(inputValue.trim())) {
      setUrlError(
        t(ACCESSIBILITY_MODAL.LINK_INVALID_URL.key, ACCESSIBILITY_MODAL.LINK_INVALID_URL.value),
      );
      return;
    }
    setUrlError('');
    updateImageRedirectUrl(inputValue.trim());
    setIsEditing(false);
  };

  const handleEdit = () => {
    setInputValue(accessibilityInfo?.hyperlinkValue || '');
    setUrlError('');
    setIsEditing(true);
  };

  const handleRemove = () => {
    setInputValue('');
    setUrlError('');
    setIsEditing(false);
    updateImageRedirectUrl('');
  };

  const linkPopoverContent = (
    <BarPopupWrapper>
      <p className="label">
        {accessibilityInfo?.hyperlinkValue && !isEditing
          ? t(ACCESSIBILITY_MODAL.LINK_VISIT_LABEL.key, ACCESSIBILITY_MODAL.LINK_VISIT_LABEL.value)
          : t(ACCESSIBILITY_MODAL.LINK_ENTER_LABEL.key, ACCESSIBILITY_MODAL.LINK_ENTER_LABEL.value)}
      </p>

      {/* Show input when no saved link OR when editing */}
      {(!accessibilityInfo?.hyperlinkValue || isEditing) && (
        <>
          <LinkInputRow>
            <Input
              value={inputValue}
              onChange={e => {
                setInputValue(e.target.value);
                if (urlError) setUrlError('');
              }}
              onPressEnter={handleSave}
              placeholder="https://example.com"
              autoFocus
            />
            <LinkSaveButton onClick={handleSave}>
              <Icon type="check" />
              {t(ACCESSIBILITY_MODAL.LINK_SAVE.key, ACCESSIBILITY_MODAL.LINK_SAVE.value)}
            </LinkSaveButton>
            {isEditing && (
              <LinkCancelButton
                onClick={() => {
                  setIsEditing(false);
                  setUrlError('');
                }}
              >
                <Icon type="close" />
              </LinkCancelButton>
            )}
          </LinkInputRow>
          {urlError && (
            <LinkErrorMessage>
              <Icon type="exclamation-circle" />
              {urlError}
            </LinkErrorMessage>
          )}
        </>
      )}

      {/* Show saved link with Edit/Remove when link exists and not editing */}
      {accessibilityInfo?.hyperlinkValue && !isEditing && (
        <LinkSavedContainer>
          <LinkDisplayCard>
            <Icon type="link" className="link-icon" />
            <LinkDisplayText title={accessibilityInfo?.hyperlinkValue || ''}>
              <a
                href={accessibilityInfo?.hyperlinkValue || ''}
                target="_blank"
                rel="noopener noreferrer"
              >
                {accessibilityInfo?.hyperlinkValue || ''}
              </a>
            </LinkDisplayText>
          </LinkDisplayCard>
          <LinkActionRow>
            <LinkEditButton onClick={handleEdit}>
              <Icon type="edit" />
              {t(ACCESSIBILITY_MODAL.LINK_EDIT.key, ACCESSIBILITY_MODAL.LINK_EDIT.value)}
            </LinkEditButton>
            <LinkRemoveButton onClick={handleRemove}>
              <Icon type="delete" />
              {t(ACCESSIBILITY_MODAL.LINK_REMOVE.key, ACCESSIBILITY_MODAL.LINK_REMOVE.value)}
            </LinkRemoveButton>
          </LinkActionRow>
        </LinkSavedContainer>
      )}
    </BarPopupWrapper>
  );

  return (
    <Container className="accessibility-icon">
      <BarContainer>
        {showAccessibility && (
          <Popover
            visible={accessibilityVisible}
            content={
              <BarPopupWrapper>
                <p className="label">
                  {t(
                    ACCESSIBILITY_MODAL.SUMMARY_SCREEN_READER_LABEL.key,
                    ACCESSIBILITY_MODAL.SUMMARY_SCREEN_READER_LABEL.value,
                  )}
                </p>
                <AccessibilityContent>
                  <AccessibilitySummaryEditor
                    key={elementUUID}
                    itemUUID={`element-summary-${elementUUID}`}
                    contents={accessibilityInfo?.summary || ''}
                    customContainerStyle={{ marginTop: 10 }}
                    onSaveChanges={updateAccessibleSummary}
                    customizeHistory={false}
                    customClassName={'summary-editor'}
                  />
                </AccessibilityContent>
              </BarPopupWrapper>
            }
            placement="rightTop"
          >
            <Tooltip
              placement="topLeft"
              title={t(
                ACCESSIBILITY_MODAL.ACCESSIBILITY_TOOLTIP.key,
                ACCESSIBILITY_MODAL.ACCESSIBILITY_TOOLTIP.value,
              )}
            >
              {!accessibilityVisible ? (
                <IconButton onClick={() => setAccessibilityPopupVisible(true)}>
                  <AccessibilitySvgIcon className="hover-icon" />
                </IconButton>
              ) : (
                <IconButton onClick={() => setAccessibilityPopupVisible(false)}>
                  <Icon type="close" className="hover-icon close-icon" />
                </IconButton>
              )}
            </Tooltip>
          </Popover>
        )}
        {showImageLink && (
          <Popover visible={linkVisible} content={linkPopoverContent} placement="rightTop">
            <Tooltip placement="topLeft" title="Redirection Link">
              {!linkVisible ? (
                <IconButton onClick={() => setImageLinkPopupVisible(true)}>
                  <Icon type="link" className="hover-icon" />
                </IconButton>
              ) : (
                <IconButton onClick={() => setImageLinkPopupVisible(false)}>
                  <Icon type="close" className="hover-icon close-icon" />
                </IconButton>
              )}
            </Tooltip>
          </Popover>
        )}
      </BarContainer>
    </Container>
  );
};

export default withTranslation(NAMESPACES)(AccessibilityIcon);
